<template>
  <v-card class="planning-create">
    <v-card-title>
      <span class="toolbar_title">Create New Plan</span>
      <v-spacer></v-spacer>
      <v-btn icon @click="closeSetup">
        <v-icon color="black">mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text class="planning-create__content">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 px-0 pt-3 pb-6">
          <v-text-field
            v-model="plan_name"
            label="Plan Name"
            outlined
            hide-details="auto"
            required
          ></v-text-field>
        </v-col>
        <v-col cols="12" class="pa-0">
          <v-row class="planning-content-layout ma-0 pa-0" align="stretch">
            <v-col cols="12" md="6" class="planning-column pa-0 pr-md-3 pb-6">
              <v-card outlined class="planning-setup-card planning-ad-unit-card">
                <v-card-title class="pb-2">
                  <div>
                    <div class="section_title">Ad Unit Definition</div>
                    <div class="section_subtitle">
                      Choose the data levels used to define the plan.
                    </div>
                  </div>
                </v-card-title>
                <v-card-text class="pt-0">
                  <div class="planning-checkbox-row">
                    <v-checkbox
                      v-model="flighted"
                      label="Flighted Optimization"
                      hide-details
                    ></v-checkbox>
                    <v-checkbox
                      v-model="flighted_only"
                      label="Flighted Only"
                      hide-details
                    ></v-checkbox>
                  </div>
                  <v-select
                    class="section_components mt-6"
                    v-model="selected_levels"
                    :items="available_data_levels"
                    label="Data Levels"
                    multiple
                    outlined
                    hint="Pick the data levels you want to use in the Ad Unit definition."
                    persistent-hint
                    chips
                    :disabled="flighted_only"
                  ></v-select>
                </v-card-text>
              </v-card>
              <v-card outlined class="planning-filters-card">
                <v-card-title class="pb-2 planning-filters-header">
                  <div>
                    <div class="section_title">Data Filter</div>
                    <div class="section_subtitle">
                      Narrow the selected source data before creating the plan.
                    </div>
                  </div>
                </v-card-title>
                <v-card-text class="planning-filters-content pt-0">
                  <v-row class="ma-0 pa-0">
                    <v-col
                      v-for="(lvl, index) in available_data_levels"
                      :key="index"
                      cols="12"
                      class="px-0"
                    >
                      <v-select
                        class="section_components"
                        v-model="filter_selected_vals[lvl]"
                        :items="project_filters[lvl]"
                        :label="lvl"
                        multiple
                        outlined
                        hide-details="auto"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" class="planning-column pa-0 pl-md-3 pb-6">
              <v-card outlined class="planning-setup-card planning-date-range-card">
                <v-card-title class="pb-2">
                  <div>
                    <div class="section_title">Blocking Chart Date Range</div>
                    <div class="section_subtitle">
                      Select the historical range used to create the plan.
                    </div>
                  </div>
                </v-card-title>
                <v-card-text class="planning-date-range-content pt-0">
                  <div class="planning-date-section">
                    <div class="scheduler_label">Range Type</div>
                    <v-tabs
                      v-model="prediction_range_tab"
                      align-tabs="start"
                      @change="on_prediction_tab_change($event)"
                    >
                      <v-tab class="prediction_range_tab" key="predefined">
                        Predefined
                      </v-tab>
                      <v-tab class="prediction_range_tab" key="customized" disabled>
                        Customized
                      </v-tab>
                    </v-tabs>
                  </div>
                  <v-tabs-items
                    v-model="prediction_range_tab"
                    class="planning-date-tabs-items"
                  >
                    <v-tab-item>
                      <v-row class="ma-0 pa-0 planning-date-form">
                        <v-col cols="12" class="pa-0">
                          <div class="scheduler_label">Duration</div>
                          <div class="planning-duration-grid">
                            <v-btn
                              v-for="option in predefined_range_options"
                              :key="option.value"
                              outlined
                              depressed
                              class="planning-duration-tile"
                              :class="{
                                'planning-duration-tile--selected':
                                  predefined_radio_val === option.value,
                              }"
                              :disabled="option.disabled"
                              @click="select_predefined_range(option.value)"
                            >
                              <span>{{ option.label }}</span>
                            </v-btn>
                          </div>
                        </v-col>
                        <v-col cols="12" class="pa-0 planning-date-picker-row">
                          <div class="scheduler_label">Month Selection</div>
                          <v-select
                            class="section_components forecast-month-select"
                            v-model="selected_month"
                            :items="month_options"
                            item-text="label"
                            item-value="value"
                            label="Month"
                            outlined
                            hide-details="auto"
                            @change="update_copy_month"
                          ></v-select>
                          <div class="forecast-plan-window">
                            <div class="scheduler_label">Date Range Summary</div>
                            <div class="forecast-plan-window__summary">
                              <div class="forecast-plan-window__item">
                                <div class="forecast-plan-window__label">
                                  Start Date
                                </div>
                                <div class="forecast-plan-window__value">
                                  {{ copy_start_date }}
                                </div>
                              </div>
                              <div class="forecast-plan-window__item">
                                <div class="forecast-plan-window__label">
                                  End Date
                                </div>
                                <div class="forecast-plan-window__value">
                                  {{ copy_end_date }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-tab-item>
                    <v-tab-item>
                      <div class="planning-date-form">
                        <div>
                          <div class="scheduler_label">Date Window</div>
                          <DateRange
                            :start_date="predict_start_date"
                            :end_date="predict_end_date"
                            :min_start_date="predict_min_start_date"
                            :max_end_date="max_end_date"
                            v-on:start_date_update="update_predict_start_date"
                            v-on:end_date_update="update_predict_end_date"
                          ></DateRange>
                        </div>
                      </div>
                    </v-tab-item>
                  </v-tabs-items>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="planning-create__actions">
      <v-spacer></v-spacer>
      <v-btn
        outlined
        color="black"
        @click="copy_range_on_click"
        class="section_action"
        :loading="page_loading"
        :disabled="create_disabled"
      >
        <span>Create</span>
      </v-btn>
      <v-spacer></v-spacer>
    </v-card-actions>
  </v-card>
</template>

<script>
import DateRange from "@/components/Utility/DateRange.vue";

import planning_api_caller from "@/javascript/forecasting.js";
import { ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE } from "@/constants/constants";

export default {
  name: "ForecastingCreate",
  props: [
    "project_id",
    "data_version",
    "data_levels",
    "project_date_range",
    "project_filters",
    "parent_id",
  ],
  components: {
    DateRange,
  },
  data: () => ({
    plan_name: "",
    page_loading: false,
    prediction_range_tab: 0,
    predefined_radio_val: "val_1",
    predict_length: 30,
    start_date: null,
    end_date: null,
    predict_start_date: null,
    predict_end_date: null,
    copy_start_date: null,
    copy_end_date: null,
    predict_min_start_date: null,
    max_end_date: null,
    filter_selected_vals: {},
    flighted: false,
    flighted_only: false,
    selected_levels: [],
    available_data_levels: [],
    selected_month: null,
  }),
  computed: {
    trimmed_plan_name() {
      return this.plan_name.trim();
    },
    create_disabled() {
      return this.trimmed_plan_name === "";
    },
    predefined_range_options() {
      return [
        { label: "10 days", value: "val_0", disabled: true },
        { label: "30 days", value: "val_1" },
        { label: "60 days", value: "val_2" },
        { label: "90 days", value: "val_3" },
        { label: "180 days", value: "val_4" },
        { label: "270 days", value: "val_5" },
      ];
    },
    month_options() {
      return [
        { label: "January", value: 0 },
        { label: "February", value: 1 },
        { label: "March", value: 2 },
        { label: "April", value: 3 },
        { label: "May", value: 4 },
        { label: "June", value: 5 },
        { label: "July", value: 6 },
        { label: "August", value: 7 },
        { label: "September", value: 8 },
        { label: "October", value: 9 },
        { label: "November", value: 10 },
        { label: "December", value: 11 },
      ];
    },
  },
  mounted() {
    this.resetComponentState();
  },
  methods: {
    resetComponentState() {
      this.plan_name = "";
      this.selected_levels = [...this.data_levels["selected_levels"]];
      if (this.selected_levels.length == 0) {
        this.selected_levels = [this.data_levels["data_levels"][0]];
      }

      this.filter_selected_vals = {};
      for (let lvl of this.data_levels["data_levels"]) {
        this.filter_selected_vals[lvl] = [...this.project_filters[lvl]];
      }

      this.available_data_levels = [...this.data_levels["data_levels"]];
      let predict_start_date = new Date();
      predict_start_date.setDate(predict_start_date.getDate() + 1);
      let formatted_start_date = predict_start_date.toISOString().slice(0, 10);

      this.predict_start_date = formatted_start_date;
      this.predict_end_date = formatted_start_date;
      this.predict_min_start_date = formatted_start_date;
      this.copy_start_date = this.project_date_range["start_date"];
      this.selected_month = this.get_date_parts(this.copy_start_date).month;
      this.copy_start_date = this.get_latest_month_start_date(this.selected_month);
      this.max_end_date = this.project_date_range["max_end_date"];
      this.prediction_range_tab = 0;
      this.predefined_radio_val = "val_1";
      this.predict_length = 30;
      this.flighted = false;
      this.flighted_only = false;
      this.update_copy_range(true);
    },
    closeSetup() {
      this.resetComponentState();
      this.$emit("close");
    },
    getFolderParentId(folderId) {
      return folderId === null || folderId === undefined ? -1 : folderId;
    },
    format_date(date) {
      return date.toISOString().slice(0, 10);
    },
    get_date_parts(dateValue) {
      const [year, month, day] = dateValue.split("-").map(Number);

      return {
        year,
        month: month - 1,
        day,
      };
    },
    format_date_parts(year, month, day) {
      return [
        year,
        String(month + 1).padStart(2, "0"),
        String(day).padStart(2, "0"),
      ].join("-");
    },
    get_latest_month_start_date(month) {
      const maxEndDate = this.get_date_parts(this.project_date_range["max_end_date"]);
      const year =
        month <= maxEndDate.month ? maxEndDate.year : maxEndDate.year - 1;

      return this.format_date_parts(year, month, 1);
    },
    update_copy_month(month) {
      this.copy_start_date = this.get_latest_month_start_date(month);
      this.update_copy_range(true);
    },
    update_copy_range(forward_update) {
      let tmpDate = null;
      if (forward_update) {
        tmpDate = new Date(this.copy_start_date);
        tmpDate.setDate(tmpDate.getDate() + this.predict_length);
        let maxEndDate = new Date(this.project_date_range["max_end_date"]);
        if (tmpDate > maxEndDate) {
          tmpDate = maxEndDate;
        }
        this.copy_end_date = this.format_date(tmpDate);
      } else {
        tmpDate = new Date(this.copy_end_date);
        tmpDate.setDate(tmpDate.getDate() - this.predict_length);
        let minStartDate = new Date(this.project_date_range["min_start_date"]);
        if (tmpDate < minStartDate) {
          tmpDate = minStartDate;
        }
        this.copy_start_date = this.format_date(tmpDate);
      }
    },
    update_copy_start_date(date) {
      if (date != this.copy_start_date) {
        this.copy_start_date = date;
        this.update_copy_range(true);
      }
    },
    update_copy_end_date(date) {
      if (date != this.copy_end_date) {
        this.copy_end_date = date;
        this.update_copy_range(false);
      }
    },
    update_predict_start_date(date) {
      this.predict_start_date = date;
      this.update_predict_length();
      this.update_copy_range(true);
    },
    update_predict_end_date(date) {
      this.predict_end_date = date;
      this.update_predict_length();
      this.update_copy_range(true);
    },
    update_predict_length() {
      const startDate = new Date(this.predict_start_date);
      const endDate = new Date(this.predict_end_date);
      const dayInMs = 24 * 60 * 60 * 1000;
      const diffDays = Math.round((endDate - startDate) / dayInMs);
      this.predict_length = Math.max(diffDays, 0);
    },
    on_prediction_tab_change(tab_idx) {
      if (tab_idx == 0) {
        this.predict_length = 30;
      } else {
        this.update_predict_length();
      }
      this.update_copy_range(true);
    },
    select_predefined_range(value) {
      if (this.predefined_radio_val === value) {
        return;
      }

      this.predefined_radio_val = value;
      this.on_predefined_input_change(value);
    },
    on_predefined_input_change(val) {
      switch (val) {
        case "val_0":
          this.predict_length = 10;
          break;
        case "val_1":
          this.predict_length = 30;
          break;
        case "val_2":
          this.predict_length = 60;
          break;
        case "val_3":
          this.predict_length = 90;
          break;
        case "val_4":
          this.predict_length = 180;
          break;
        case "val_5":
          this.predict_length = 270;
          break;
      }
      this.update_copy_range(true);
    },
    copy_range_on_click: async function () {
      if (this.create_disabled) {
        return;
      }

      this.page_loading = true;
      this.start_date = this.copy_start_date;
      this.end_date = this.copy_end_date;

      let forecasting_report_payload = {};
      if (this.flighted_only) {
        forecasting_report_payload = {
          project_id: this.project_id,
          parent_id: this.getFolderParentId(this.parent_id),
          plan_name: this.trimmed_plan_name,
          data_version: this.data_version,
          start_date: this.start_date,
          end_date: this.end_date,
          ad_unit: [],
          flighted: true,
          data_filters: this.filter_selected_vals,
        };
      } else {
        forecasting_report_payload = {
          project_id: this.project_id,
          parent_id: this.getFolderParentId(this.parent_id),
          plan_name: this.trimmed_plan_name,
          data_version: this.data_version,
          start_date: this.start_date,
          end_date: this.end_date,
          ad_unit: this.selected_levels,
          flighted: this.flighted,
          data_filters: this.filter_selected_vals,
        };
      }

      let token = await this.$auth.getTokenSilently();
      let result = await planning_api_caller
        .create_campaign_plan(token, forecasting_report_payload)
        .then((resp) => {
          if (resp["success"]) {
            return resp["data"];
          }
          return false;
        })
        .catch(() => {
          return false;
        });

      if (result && result["campaign_plan_id"]) {
        this.$emit("plan_created");
        this.$emit(
          "update_campaign_plan_id",
          result["campaign_plan_id"],
          this.trimmed_plan_name,
          {
            data_levels: forecasting_report_payload.ad_unit,
            data_filters: forecasting_report_payload.data_filters,
          }
        );
        this.$emit("date_update", {
          start_date: this.start_date,
          end_date: this.end_date,
          duration_days: this.predict_length,
        });
        this.resetComponentState();
        this.$emit("close");
      } else {
        this.$emit("triggerAlert", ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE);
      }

      this.page_loading = false;
    },
  },
};
</script>

<style scoped>
.planning-create {
  display: flex;
  flex-direction: column;
  font-family: "Poppins" !important;
  height: 90vh;
  max-height: calc(100vh - 48px);
  overflow: hidden;
  width: 100%;
}

.planning-create ::v-deep .v-card__title,
.planning-create ::v-deep .v-card__text,
.planning-create ::v-deep .v-card__actions,
.planning-create ::v-deep .v-btn,
.planning-create ::v-deep .v-chip,
.planning-create ::v-deep .v-input,
.planning-create ::v-deep .v-input input,
.planning-create ::v-deep .v-input label,
.planning-create ::v-deep .v-label,
.planning-create ::v-deep .v-list-item__title,
.planning-create ::v-deep .v-select__selection,
.planning-create ::v-deep .v-messages,
.planning-create ::v-deep .v-tab {
  font-family: "Poppins" !important;
}

.toolbar_title {
  font-family: "Poppins" !important;
  font-weight: 600;
}

.planning-create > .v-card__title,
.planning-create > .v-card__actions {
  flex: 0 0 auto;
}
.planning-create__content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0px 16px;
}

.planning-setup-card,
.planning-create__actions,
.planning-filters-card {
  border-radius: 12px;
}

.planning-content-layout {
  height: calc(100% - 80px);
  min-height: 430px;
}

.planning-column {
  display: flex;
  flex-direction: column;
}

.planning-setup-card,
.planning-filters-card {
  width: 100%;
}

.planning-ad-unit-card {
  margin-bottom: 12px;
}

.planning-date-range-card,
.planning-filters-card {
  flex: 1 1 auto;
  min-height: 0;
}

.planning-date-range-card {
  display: flex;
  flex-direction: column;
}

.planning-date-range-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
}

.planning-date-section {
  margin-bottom: 32px;
}

.planning-date-tabs-items {
  flex: 1 1 auto;
}

.planning-date-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.planning-date-picker-row {
  margin-top: 0;
}

.planning-filters-card {
  display: flex;
  flex-direction: column;
}

.planning-filters-header {
  flex: 0 0 auto;
}

.planning-filters-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.planning-checkbox-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
}

.planning-duration-grid {
  display: grid;
  gap: 14px 16px;
  grid-template-columns: repeat(3, minmax(120px, 1fr));
}

.planning-duration-tile {
  border-color: rgba(0, 0, 0, 0.28) !important;
  font-family: "Poppins" !important;
  font-size: 0.92rem;
  font-weight: 500;
  height: 54px !important;
  justify-content: flex-start;
  padding: 0 18px !important;
  text-transform: none;
}

.planning-duration-tile--selected {
  background: #f4f6f7 !important;
  border-color: rgba(0, 0, 0, 0.86) !important;
  border-width: 2px !important;
  color: rgba(0, 0, 0, 0.9) !important;
  font-weight: 600;
}

.scheduler_label {
  color: rgba(0, 0, 0, 0.6);
  font-family: "Poppins" !important;
  font-size: 0.76rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.forecast-month-select ::v-deep .v-select__selection,
.forecast-month-select ::v-deep input {
  font-size: 1.05rem;
}

.forecast-plan-window {
  margin-top: 32px;
}

.forecast-plan-window__summary {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 16px 18px;
}

.forecast-plan-window__item {
  min-width: 0;
}

.forecast-plan-window__label {
  color: rgba(0, 0, 0, 0.55);
  font-family: "Poppins" !important;
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.forecast-plan-window__value {
  color: rgba(0, 0, 0, 0.86);
  font-family: "Poppins" !important;
  font-size: 1.08rem;
  font-weight: 600;
}

.section_title {
  font-family: "Poppins" !important;
  font-weight: 600;
  font-size: 1rem;
}

.section_subtitle {
  font-family: "Poppins" !important;
  font-weight: 400;
  font-size: 0.85rem;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.6);
}

.section_components {
  font-family: "Poppins" !important;
  font-weight: 400;
  font-size: 0.9em;
}

.section_action {
  font-family: "Poppins" !important;
  font-weight: 400;
  font-size: 1em;
  width: 200px;
}

.prediction_range_tab {
  font-size: x-small;
  font-family: "Poppins" !important;
  font-weight: 600 !important;
}

@media (max-width: 960px) {
  .planning-content-layout {
    height: auto;
    min-height: 0;
  }

  .planning-date-range-card,
  .planning-filters-card {
    flex: 0 0 auto;
  }

  .planning-duration-grid {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

}
</style>
